import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { StylesProvider, jssPreset } from "@material-ui/core/styles";
import { create } from "jss";

import Input from "../components/Input";
import DatePicker from "../components/DatePicker";
import DateRange from "../components/DateRange";
import StepHeader from "../components/StepHeader";
import Button from "../components/Button";

import Animate from "../Animate";

import { getNumber } from "../utils/numberParsers";
import validator from "../utils/validators";
import { FieldShape, ContentWithVarsShape } from "../utils/types";

const parseNumberValues = (values, fields) => {
  const numericFields = fields.filter((f) => f.type === "number");
  const parsedValues = { ...values };

  numericFields.forEach((f) => {
    parsedValues[f.id] = getNumber(parsedValues[f.id]);
  });

  return parsedValues;
};

export default function FormStep({
  id,
  fields = [],
  title = "",
  description = "",
  htmlContent = "",
  onSubmit,
  submitLabel = "",
  styleElement,
  variables,
  emptyVariableFormatter,
  previewMode = false,
}) {
  const transformNumericValues = React.useCallback(
    (rawValues) => parseNumberValues(rawValues, fields),
    [fields],
  );

  const formik = useFormik({
    initialValues: fields.reduce(
      (acc, field) => ({
        [field.id]: field.initialValue || "",
        ...acc,
      }),
      {},
    ),
    onSubmit: (submitValues) => {
      const parsedValues = transformNumericValues(submitValues);

      onSubmit(parsedValues, id);
    },
    validate: (submitValues) => {
      const validationErrors = fields.reduce((acc, field) => {
        const errorObject = { ...acc };
        const value = submitValues[field.id];

        if (value === "" || value === undefined || value === false) {
          if (field.required !== true) {
            /*
             * Being empty and not required => already valid
             */
            return errorObject;
          }
          errorObject[field.id] = "El campo es obligatorio";

          return errorObject;
        }

        if (!field.validation) {
          return errorObject;
        }

        if (typeof field.validation === "function") {
          if (!field.validation(value)) {
            errorObject[field.id] = true;
          }
        } else {
          const errorMessage = validator(value, field.validation);

          if (errorMessage !== undefined) {
            errorObject[field.id] = errorMessage;
          }
        }

        return errorObject;
      }, {});

      return validationErrors;
    },
  });
  const {
    values,
    handleSubmit: formikHandleSubmit,
    setFieldValue,
    validateForm,
    errors,
    submitCount,
  } = formik;

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jss = React.useMemo(() => {
    return create({
      ...jssPreset(),
      insertionPoint: styleElement,
    });
  }, [styleElement]);

  return (
    <Animate>
      <StylesProvider jss={jss}>
        <div className="grid">
          <div className="container">
            <form onSubmit={handleSubmit} noValidate>
              <StepHeader
                title={title}
                description={description}
                htmlContent={htmlContent}
                variables={variables}
                emptyVariableFormatter={emptyVariableFormatter}
              />

              {fields.map((field) => {
                const {
                  id: fieldId,
                  label,
                  type,
                  htmlLabel,
                  required,
                  helperText,
                  csvData,
                } = field;
                const value = values[fieldId];

                const errorMessage = submitCount > 0 ? errors[fieldId] : false;
                const error = !!errorMessage;

                const inputHandleChange = (event) => {
                  const newValue =
                    type === "checkbox" ? event.target.checked : event.target.value;

                  setFieldValue(fieldId, newValue);
                };

                if (["date", "datetime", "datetime-local"].includes(type)) {
                  return (
                    <DatePicker
                      {...field}
                      key={fieldId}
                      error={error}
                      helperText={errorMessage}
                      id={fieldId}
                      name={fieldId}
                      label={label}
                      onChange={inputHandleChange}
                      value={value}
                      type={type}
                    />
                  );
                } else if (type === "daterange") {
                  return (
                    <DateRange
                      {...field}
                      key={fieldId}
                      error={error}
                      helperText={errorMessage}
                      id={fieldId}
                      name={fieldId}
                      label={label}
                      onChange={inputHandleChange}
                      value={value}
                      type={type}
                      previewMode={previewMode}
                    />
                  );
                }

                const autoComplete = type === "email" ? "email" : "off";
                const name = type === "email" ? "email" : fieldId;

                return (
                  <Input
                    key={fieldId}
                    error={error}
                    helperText={errorMessage || helperText}
                    autoComplete={autoComplete}
                    name={name}
                    label={label}
                    htmlLabel={htmlLabel}
                    id={fieldId}
                    onChange={inputHandleChange}
                    value={value}
                    csvData={csvData}
                    type={type}
                    required={required}
                    previewMode={previewMode}
                  />
                );
              })}

              <Button label={submitLabel} sticky={true} type="submit" />
            </form>
          </div>
        </div>
      </StylesProvider>
    </Animate>
  );

  function handleSubmit(value) {
    const fieldsWithVars = fields.filter((field) => field.useAsVariable);

    fieldsWithVars.forEach((fieldWithVar) => {
      const variable = variables[id][fieldWithVar.id];

      if (variable) {
        sessionStorage.setItem(
          `CUSTOM_VARIABLE_${variable.name}`,
          values[fieldWithVar.id],
        );
      }
    });

    formikHandleSubmit(value, id);
  }
}

FormStep.propTypes = {
  id: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(FieldShape),
  title: ContentWithVarsShape,
  description: ContentWithVarsShape,
  htmlContent: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  styleElement: PropTypes.object,
  variables: PropTypes.object.isRequired,
  emptyVariableFormatter: PropTypes.func,
  previewMode: PropTypes.bool,
};
